import { computed, onScopeDispose, ref, toRaw, watch } from 'vue'
import type { VpnFirstReferralOffer } from '../types/vpn'

// The same cached response may outlive the component. Remounting must not
// restart its clock. A fresh response supplies a fresh server_now on reload.
const receivedAt = new WeakMap<VpnFirstReferralOffer, number>()

/**
 * Шаг часов. Секунда была бы достаточна для самой подписи, но отсчёт показывает
 * ещё и миллисекунды — они обязаны мелькать, иначе не отличаются от картинки.
 * Двадцать кадров в секунду хватает глазу и не превращает страницу в анимацию.
 */
const TICK_MS = 50

export function rememberReferralOffer(offer: VpnFirstReferralOffer): number {
  const raw = toRaw(offer)
  if (!receivedAt.has(raw)) receivedAt.set(raw, performance.now())
  return receivedAt.get(raw)!
}

/**
 * Остаток окна в целых миллисекундах — ЕДИНСТВЕННАЯ арифметика часов. И
 * подпись, и число секунд считаются из неё, поэтому разойтись им негде.
 */
export function referralMsLeft(offer: VpnFirstReferralOffer, elapsedMs: number): number {
  if (offer.state !== 'available' || !offer.expires_at) return 0
  const duration = Date.parse(offer.expires_at) - Date.parse(offer.server_now)
  if (!Number.isFinite(duration) || !Number.isFinite(elapsedMs)) return 0
  return Math.max(0, Math.floor(duration - Math.max(0, elapsedMs)))
}

export function referralSecondsLeft(offer: VpnFirstReferralOffer, elapsedMs: number): number {
  return Math.ceil(referralMsLeft(offer, elapsedMs) / 1000)
}

export function useFirstReferralOffer(source: () => VpnFirstReferralOffer | null | undefined) {
  const left = ref(0)
  let interval: ReturnType<typeof setInterval> | undefined
  const stop = () => {
    if (interval !== undefined) clearInterval(interval)
    interval = undefined
  }
  const valid = computed(() => {
    const offer = source()
    return !!offer && Number.isFinite(offer.bonus_days) && offer.bonus_days > 0 &&
      (offer.state !== 'available' || (Number.isFinite(Date.parse(offer.expires_at ?? '')) && Number.isFinite(Date.parse(offer.server_now))))
  })
  watch(source, offer => {
    stop()
    left.value = 0
    if (!offer || !valid.value || offer.state !== 'available') return
    const start = rememberReferralOffer(offer)
    const tick = () => {
      left.value = referralMsLeft(offer, performance.now() - start)
      if (left.value === 0) stop()
    }
    tick()
    if (left.value > 0) interval = setInterval(tick, TICK_MS)
  }, { immediate: true })
  onScopeDispose(stop)

  /**
   * Сколько секунд окно ещё ЖИВО — округление вверх: пока остался хоть
   * миллисекунда, окно открыто. Это признак состояния, а не подпись.
   */
  const seconds = computed(() => Math.ceil(left.value / 1000))
  const state = computed(() => {
    const offer = source()
    if (!valid.value || !offer) return 'unavailable'
    return offer.state === 'available' && seconds.value === 0 ? 'expired' : offer.state
  })
  /**
   * Подпись часов — округление ВНИЗ: рядом стоят миллисекунды, и «00:00:03.500»
   * при двух с половиной секундах остатка было бы прямым враньём.
   */
  const countdown = computed(() => {
    const value = Math.floor(left.value / 1000)
    return [Math.floor(value / 3600), Math.floor(value / 60) % 60, value % 60]
      .map(part => String(part).padStart(2, '0')).join(':')
  })
  const millis = computed(() => String(left.value % 1000).padStart(3, '0'))
  return { state, countdown, millis, seconds }
}
